/**
 * The publish block inside the share dialog.
 *
 * It renders ONLY where the deployment really has the LinkedIn integration configured — the status
 * is asked before anything is offered — so an unconfigured deployment, and the serverless demo, show
 * exactly what they showed before: no connect button, no publish button, nothing that could look
 * like a working automatic share.
 *
 * ONE WINDOW, NO ROUND TRIP
 * -------------------------
 * The member sees the picture and their own text, connects their account if it is not already
 * connected, and the SAME view then offers the control that publishes — with the account it will
 * post as, and the fact that the post is public, written directly above it. There is no «review the
 * post» step and no page to go back from: going forward to edit a word and coming back was a round
 * trip through a screen that said nothing the member could not already see.
 *
 * Exactly one control in the whole product creates a post, and it is the one that says «Onayla ve
 * LinkedIn'de Yayımla». Coming back from LinkedIn publishes nothing; opening this dialog publishes
 * nothing; connecting an account publishes nothing.
 *
 * WHAT SURVIVES THE DIALOG BEING CLOSED
 * -------------------------------------
 * A connection does. It used to live in this component's own state, so closing the dialog without
 * publishing threw it away and the member had to go through LinkedIn again to say the same thing —
 * and, worse, the second attempt started a NEW authorization on the server, which stands the first
 * connection down. `linkedin-member-post.ts` keeps the draft and session in tab memory, keyed by
 * record, so reopening finds the account already connected. It is memory, for the life of the tab:
 * never `localStorage`, never `sessionStorage`, never a URL.
 *
 * And an ABANDONED attempt stops. Closing the dialog mid-connect aborts the wait, which removes its
 * listener — otherwise the next attempt's one-time hand-over was received by both and spent by
 * whichever asked first.
 *
 * Nothing here holds a token. The draft secret and the session token are opaque values the server
 * issued, and they never reach storage or an address bar.
 */
import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import {
  AUTHORIZATION_WINDOW_NAME,
  connectMember,
  forgetConnection,
  type MemberPostDraft,
  MemberPostError,
  type MemberPostFailure,
  type MemberPostSession,
  memberPostApiOrigin,
  memberPostAvailability,
  newIdempotencyKey,
  openDraft,
  type PublishedPost,
  publishPost,
  rememberConnection,
  rememberedConnection,
} from '../lib/linkedin-member-post';
import type { ShareStrings } from '../lib/public-page';

/**
 * `unavailable` is the state this file did not have.
 *
 * `off` means the deployment answered and said the integration is not configured — a real product
 * state, and the link share beside it is the right offer. `unavailable` means nothing was
 * established: the status request failed, or this page could not work out which API to ask. Those
 * were the same `off` before, so a momentary failure silently presented the link-only dialog as
 * though it were the product. It now says so and offers to ask again.
 */
type Phase =
  | 'checking'
  | 'off'
  | 'unavailable'
  | 'ready'
  | 'connecting'
  | 'connected'
  | 'publishing'
  | 'published';

/**
 * What the dialog around this block needs to know to draw itself.
 *
 * `enabled` is false until the status answer says otherwise, so a deployment without the
 * integration — and every frame before the answer arrives — draws exactly the dialog it drew
 * before this flow existed.
 */
export interface MemberPostState {
  enabled: boolean;
  /**
   * WHY the dialog is in the shape it is in, which `enabled` alone cannot say.
   *
   * `checking` — no answer yet, so nothing is offered and nothing is claimed.
   * `disabled` — the deployment answered «not configured»; the link share is the honest offer.
   * `unavailable` — the status could not be established; the link share is an ALTERNATIVE, said as
   * one, rather than a substitute presented as the product.
   * `enabled` — the publish flow is the offer.
   */
  availability: 'checking' | 'disabled' | 'unavailable' | 'enabled';
  connected: boolean;
  memberName: string | null;
  /**
   * The record's canonical public address AS THE SERVER GAVE IT for this draft — null until a draft
   * exists. The dialog needs it because the post's text is not always the contents of the text box:
   * the server appends this address when the member has not written it, and a dialog that does not
   * know the address cannot say what will actually be published.
   */
  canonicalUrl: string | null;
  /** True while a connection or a publish is actually in progress. */
  busy: boolean;
}

export function LinkedInMemberPost({
  publicCode,
  text,
  strings,
  publishable,
  onStateChange,
}: {
  publicCode: string;
  /** The text as the member has it right now; read at publish time, never cached behind their back. */
  text: string;
  strings: ShareStrings;
  /**
   * False when the member's text cannot become a post at all — empty, over the limit, or carrying a
   * character the server refuses. The dialog decides this with the SAME rule the server uses, so a
   * text this block offers to publish is a text the server accepts.
   */
  publishable: boolean;
  onStateChange?: (state: MemberPostState) => void;
}): ReactElement | null {
  /* A connection this tab already made for this record — the reason reopening the dialog does not
     send the member back through LinkedIn. Read once, at mount. */
  const remembered = useRef(rememberedConnection(publicCode)).current;

  const [phase, setPhase] = useState<Phase>('checking');
  const [failure, setFailure] = useState<MemberPostFailure | null>(null);
  const [session, setSession] = useState<MemberPostSession | null>(remembered?.session ?? null);
  const [post, setPost] = useState<PublishedPost | null>(remembered?.published ?? null);
  const apiOrigin = useRef<string | null>(memberPostApiOrigin());
  const draft = useRef<MemberPostDraft | null>(remembered?.draft ?? null);
  /* One key per attempt, kept across a retry so a second press cannot become a second post. */
  const idempotencyKey = useRef<string | null>(remembered?.idempotencyKey ?? null);
  const textRef = useRef(text);
  textRef.current = text;

  /*
   * The attempt in progress, so an abandoned one cannot write over the one that replaced it.
   *
   * `abort` ends the wait; `generation` is what a resolved promise checks before it touches state.
   * Both are needed: aborting stops the polling, and the generation stops an answer that was
   * already in flight when the abort happened.
   */
  const attempt = useRef<AbortController | null>(null);
  const generation = useRef(0);

  const busy = phase === 'connecting' || phase === 'publishing';
  const enabled = phase !== 'checking' && phase !== 'off' && phase !== 'unavailable';
  const availability: MemberPostState['availability'] =
    phase === 'checking'
      ? 'checking'
      : phase === 'off'
        ? 'disabled'
        : phase === 'unavailable'
          ? 'unavailable'
          : 'enabled';
  const memberName = session ? session.memberName : null;
  /*
   * Held in state as well as in the ref, because the dialog above has to re-render when it arrives:
   * the address is what turns the member's text into the post, and the dialog shows it.
   */
  const [canonicalUrl, setCanonicalUrl] = useState<string | null>(remembered?.draft?.canonicalUrl ?? null);
  useEffect(() => {
    onStateChange?.({
      enabled,
      availability,
      connected: session !== null,
      memberName,
      canonicalUrl,
      busy,
    });
  }, [enabled, availability, session, memberName, canonicalUrl, busy, onStateChange]);

  /* Bumped by «Yeniden dene»; asking again is the whole remedy for a transient failure. */
  const [statusAttempt, setStatusAttempt] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `statusAttempt` is not read in the effect; it IS the retry signal, and bumping it is what re-asks
  useEffect(() => {
    const origin = apiOrigin.current;
    /*
     * No origin at all is NOT «the integration is off». It is this page not knowing which API to
     * ask — the demo, where that is the truth and `off` is right, and a server-rendered page whose
     * head came back without `og:image`, where it is not. The two are told apart by whether the
     * build names an API: `memberPostApiOrigin` answers null only when nothing does.
     */
    if (!origin) {
      setPhase('off');
      return;
    }
    let live = true;
    setPhase('checking');
    void memberPostAvailability(origin).then((answer) => {
      if (!live) return;
      /* A connection this tab already made is where the member picks up, not the connect button. */
      setPhase(
        answer !== 'enabled'
          ? answer === 'disabled'
            ? 'off'
            : 'unavailable'
          : draft.current && session
            ? 'connected'
            : 'ready',
      );
    });
    return () => {
      live = false;
    };
  }, [statusAttempt, session]);

  /* Read by the unmount handler below, which must see the LAST values rather than the first ones. */
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const postRef = useRef(post);
  postRef.current = post;

  /*
   * Closing the dialog ends the attempt and keeps the connection.
   *
   * The abort is what stops an abandoned wait from polling on and from spending the NEXT attempt's
   * hand-over. What is remembered is what the member would otherwise have to earn again: the draft
   * the server already knows, the session it already opened, and the idempotency key, so a post
   * that may already exist can never be made twice by reopening.
   */
  useEffect(() => {
    return () => {
      attempt.current?.abort();
      attempt.current = null;
      if (draft.current)
        rememberConnection(publicCode, {
          draft: draft.current,
          session: sessionRef.current,
          idempotencyKey: idempotencyKey.current,
          published: postRef.current,
        });
    };
  }, [publicCode]);

  /**
   * Every ending this flow can have, in one place — including the ones that must not be kept.
   *
   * `expired` and `binding_unverified` mean the SERVER will not accept this draft or this
   * connection again: an unknown draft, a timed-out one, a return that could not be tied to this
   * browser. Holding on to it made every later press fail in exactly the same way, with no way
   * forward short of reloading the page — which is what «tekrar bağlanmak istediğinde bağlantı
   * kurulamıyor» looks like from the outside. So the draft, the key and the remembered connection
   * are dropped and the member is put back in front of the connect button, where the next press
   * opens a fresh draft and a fresh authorization.
   *
   * A plain `failed` is NOT that case. A publish that failed once may well succeed on a retry, and
   * the idempotency key has to survive it: renewing the key is how one post becomes two.
   */
  const fail = useCallback(
    (e: unknown, back: Phase) => {
      const reason = e instanceof MemberPostError ? e.failure : 'failed';
      /* Nothing is shown for an attempt this application abandoned: the member closed the dialog or
         started another one, and neither is news. */
      if (reason === 'cancelled') return;
      setFailure(reason);
      /* `start_refused` joins the two endings the server will not accept again: the draft and its
         ticket are finished either way, so the next press has to open fresh ones rather than
         re-presenting what was just refused. */
      if (reason === 'expired' || reason === 'binding_unverified' || reason === 'start_refused') {
        draft.current = null;
        idempotencyKey.current = null;
        setCanonicalUrl(null);
        setSession(null);
        forgetConnection(publicCode);
        setPhase('ready');
        return;
      }
      setPhase(back);
    },
    [publicCode],
  );

  const connect = useCallback(() => {
    const origin = apiOrigin.current;
    if (!origin) return;
    setFailure(null);
    /*
     * The previous attempt stands down FIRST, before a new one exists.
     *
     * Without this the old wait kept its `message` listener, received the hand-over meant for this
     * attempt, and spent it against a draft the member had walked away from — after which this
     * attempt waited for a one-time value that had already been redeemed.
     */
    attempt.current?.abort();
    const controller = new AbortController();
    attempt.current = controller;
    generation.current += 1;
    const mine = generation.current;
    /*
     * Opened inside the click itself. A window opened after an `await` is the one a browser
     * refuses, and the refusal is reported rather than left as a dialog that seems to hang.
     */
    const popup = window.open('', AUTHORIZATION_WINDOW_NAME, 'width=600,height=760');
    setPhase('connecting');
    void (async () => {
      try {
        if (!draft.current) {
          const opened = await openDraft(origin, publicCode, textRef.current);
          if (mine !== generation.current) return;
          draft.current = opened;
          setCanonicalUrl(opened.canonicalUrl);
        }
        const opened = await connectMember(origin, draft.current, popup, {
          signal: controller.signal,
        });
        if (mine !== generation.current) return;
        setSession(opened);
        /*
         * Connected, and nothing else. The member is put back exactly where they were — in front
         * of their own text — and the post is published only when they press the control that
         * says so. Returning from LinkedIn is not consent to publish.
         */
        setPhase('connected');
      } catch (e) {
        if (mine !== generation.current) return;
        /*
         * The window is NOT closed here.
         *
         * When the authorization fails, that window is showing the callback's own explanation of
         * why — and this line used to reach across and shut it, undoing the very thing the
         * callback page was changed to do. Whether the opener link survived or COOP severed it,
         * the page stays up; this dialog shows its own message beside it and stops waiting.
         *
         * The one window this flow does close is the blank one, and it is closed in
         * `connectMember` before it has been pointed anywhere — there is nothing on it to read.
         */
        fail(e, 'ready');
      }
    })();
  }, [publicCode, fail]);

  /**
   * The one call that creates a post, and the gesture rules that stand in front of it.
   *
   * `detail` is how many clicks the browser counts in the sequence this one belongs to. A plain
   * press is 1; a keyboard activation is 0; the second half of a double-click is 2. That matters
   * here because the control that connects and the control that publishes stand in the same place:
   * a member who double-clicks «bağla» would otherwise have the second click land on «yayımla»
   * the instant the connection completed. The second half of a gesture aimed at another control is
   * not consent, so it is refused — and a member who genuinely double-clicks «yayımla» still
   * publishes, once, on the first of their two presses.
   */
  const publish = useCallback(
    (event?: { detail?: number }) => {
      if ((event?.detail ?? 0) > 1) return;
      const origin = apiOrigin.current;
      const current = draft.current;
      if (!origin || !current || !session) return;
      setFailure(null);
      setPhase('publishing');
      if (!idempotencyKey.current) idempotencyKey.current = newIdempotencyKey();
      void (async () => {
        try {
          const result = await publishPost(
            origin,
            current,
            session,
            textRef.current,
            idempotencyKey.current as string,
          );
          /*
           * «Gönderi yayımlandı.» IS A CLAIM ABOUT LINKEDIN, so it waits for LinkedIn's own answer.
           *
           * The adapter already refuses to invent one — a reply without `x-restli-id` becomes
           * `result_unknown` before it can reach a response (`http-linkedin-api.ts`) — but this box
           * is the surface that makes the claim to the member, and it used to be satisfied by the
           * mere presence of a parsed object. A 200 carrying an empty id would have drawn the
           * success sentence over a dead link, and nothing here would have noticed.
           *
           * So the id and the address are checked where they are read, and the one honest outcome
           * for a success we cannot evidence is the one the vocabulary already has: the post may
           * exist, we do not know, and nothing is retried automatically. The key is NOT renewed,
           * for the same reason as every other unknown result.
           */
          if (!result.postId || !result.postUrl) throw new MemberPostError('result_unknown');
          setPost(result);
          setPhase('published');
        } catch (e) {
          /*
           * The key is NOT renewed: if the result was unknown, a new key could publish twice. The
           * member also stays where they are, with their own text where they left it, so a failure
           * costs them nothing they had written.
           */
          fail(e, session ? 'connected' : 'ready');
        }
      })();
    },
    [session, fail],
  );

  if (phase === 'checking' || phase === 'off') return null;

  /*
   * Could not be established — said, not substituted.
   *
   * No connect button, because nothing is known to connect to; no claim that the integration is
   * off, because that was not established either. The link share stands beside this, drawn by the
   * dialog and labelled as what it is.
   */
  if (phase === 'unavailable') {
    return (
      <div className="field" data-hpw-member-post="unavailable">
        <div className="warn-box" role="alert" data-hpw-member-post-error="unavailable">
          {strings.statusUnavailable}
        </div>
        <div className="share-actions">
          <button
            className="btn btn-secondary btn-sm"
            type="button"
            id="liStatusRetry"
            onClick={() => setStatusAttempt((n) => n + 1)}
          >
            {strings.statusRetry}
          </button>
        </div>
      </div>
    );
  }

  const message = failure
    ? {
        denied: strings.errDenied,
        popup_blocked: strings.errPopup,
        expired: strings.errExpired,
        start_refused: strings.errStartRefused,
        binding_unverified: strings.errBindingUnverified,
        timed_out: strings.errTimedOut,
        /* A rate limit never reaches the reader as an ending: the connect loop waits it out and
           the publish call does not repeat. If it ever does surface, it says so plainly. */
        rate_limited: strings.errFailed,
        result_unknown: strings.errUnknown,
        /* The opposite of `result_unknown`: nothing was created, and it says so, because the member
           deciding whether to press again needs that difference more than any other. */
        not_started: strings.errNotStarted,
        not_configured: strings.errFailed,
        unavailable: strings.errFailed,
        /* Never shown — `fail` returns before setting it. Listed so the map stays total. */
        cancelled: '',
        failed: strings.errFailed,
      }[failure]
    : null;

  /* A text the server would refuse is never offered for publishing. */
  const blocked = !publishable;

  return (
    <div className="field" data-hpw-member-post={phase} data-hpw-share-step="compose">
      {message ? (
        <div className="warn-box" role="alert" data-hpw-member-post-error={failure}>
          {message}
        </div>
      ) : null}
      {phase === 'published' && post ? (
        <div className="info-box" role="status" data-hpw-member-post-success="1">
          <b>{strings.published}</b>{' '}
          {/* An id, for the same reason `liConnect` and `liPublish` have one: «the created post's
              link is offered» is an acceptance criterion, and the only selector for it was a
              translated sentence. The address itself is LinkedIn's, built from the id it
              returned. */}
          <a href={post.postUrl} target="_blank" rel="noopener" id="liPostLink">
            {strings.viewPost}
          </a>
        </div>
      ) : null}
      {session ? (
        <>
          {/*
           * The two facts that belong immediately above the control that publishes: whose account
           * this posts as, and that the post is public. No page turn stands between reading them
           * and pressing the button.
           */}
          <div className="info-box" data-hpw-publish-facts="1">
            <span data-hpw-member-name="1">{`${strings.connectedAs} ${session.memberName}`}</span>
            <div style={{ marginTop: '6px' }}>
              <b>{strings.publicNotice}</b>
            </div>
          </div>
          <div className="share-actions">
            {/*
             * `key` is the point, not decoration.
             *
             * React reconciles this row by position, so without distinct keys the very DOM node
             * that was «bağla» becomes this button — the member's pointer is already on it, and
             * the second half of a double-click would publish. Keyed, the connect control is
             * unmounted and this one is a new element.
             */}
            <button
              key="publish"
              className="btn li-btn li-btn-brand btn-sm"
              type="button"
              id="liPublish"
              disabled={phase === 'publishing' || phase === 'published' || blocked}
              onClick={(event) => publish(event)}
            >
              <span className="in">in</span>
              {phase === 'publishing' ? strings.publishing : strings.publish}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="share-actions">
            <button
              key="connect"
              className="btn li-btn li-btn-brand btn-sm"
              type="button"
              id="liConnect"
              disabled={phase === 'connecting'}
              onClick={connect}
            >
              <span className="in">in</span>
              {phase === 'connecting' ? strings.connecting : strings.connect}
            </button>
          </div>
          {/* Directly under the button it is about, and only while a connection is actually wanted. */}
          <p className="help" data-hpw-connect-needed="1">
            {strings.connectNeeded}
          </p>
        </>
      )}
    </div>
  );
}
